import React, { useState, useEffect } from 'react';
import { Row, Col, Pagination, Empty } from 'antd';
import { AiFillFolder } from 'react-icons/ai';
import { useTheme } from '../../../contexts/ThemeContext';
import ProjectCard from './ProjectCard';
import Card from '../Card';

const ProjectCardGrid = ({
  projects = [],
  loading = false,
  onAction,
  onTagClick,
  onPageChange, // Callback para paginação remota
  pageSize = 12,
  totalItems, // Total de itens para paginação remota
  currentPage, // Página atual para paginação remota
  showPagination = true,
  emptyText = 'Nenhum projeto encontrado',
  emptyDescription = 'Crie seu primeiro projeto para começar',
  style,
  className
}) => {
  const { theme } = useTheme();
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const [paginatedProjects, setPaginatedProjects] = useState([]);

  // Determinar se é paginação remota ou local
  const isRemotePagination = !!onPageChange;
  const activePage = isRemotePagination ? (currentPage || 1) : localCurrentPage;
  const totalCount = isRemotePagination ? (totalItems || projects.length) : projects.length;

  // Para paginação local, calcular projetos paginados
  useEffect(() => {
    if (!isRemotePagination) {
      const startIndex = (localCurrentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setPaginatedProjects(projects.slice(startIndex, endIndex));
    } else {
      // Para paginação remota, mostrar todos os projetos recebidos
      setPaginatedProjects(projects);
    }
  }, [projects, localCurrentPage, pageSize, isRemotePagination]);

  // Resetar página quando projetos mudarem (apenas para paginação local)
  useEffect(() => {
    if (!isRemotePagination) {
      setLocalCurrentPage(1);
    }
  }, [projects.length, isRemotePagination]);

  // Handler para mudança de página
  const handlePageChange = (page, newPageSize) => {
    if (isRemotePagination) {
      // Paginação remota - chamar callback
      onPageChange(page, newPageSize || pageSize);
    } else {
      // Paginação local
      setLocalCurrentPage(page);
    }
    
    // Scroll suave para o topo do grid
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Renderizar estado vazio
  if (!loading && projects.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        ...style
      }}>
        <Empty
          image={<AiFillFolder style={{ fontSize: 64, color: theme.textSecondary, opacity: 0.3 }} />}
          description={
            <div>
              <p style={{ fontSize: 16, marginBottom: 8, color: theme.textSecondary }}>
                {emptyText}
              </p>
              <p style={{ fontSize: 14, color: theme.textSecondary }}>
                {emptyDescription}
              </p>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div className={className} style={style}>
      {/* Grid de cards */}
      <Row gutter={[24, 24]}>
        {(loading ? Array.from({ length: pageSize }, (_, index) => index) : paginatedProjects).map((project, index) => (
          <Col key={loading ? `loading-${index}` : project.id} xs={24} sm={12} md={8} lg={6}>
            {loading ? (
              // Skeleton loading card
              <Card loading style={{ height: 280 }} />
            ) : (
              <ProjectCard
                project={project}
                onAction={onAction}
                onTagClick={onTagClick}
              />
            )}
          </Col>
        ))}
      </Row>

      {/* Paginação */}
      {showPagination && totalCount > pageSize && !loading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 32
        }}>
          <Pagination
            current={activePage}
            total={totalCount}
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger={isRemotePagination} // Permitir alterar tamanho apenas para paginação remota
            showQuickJumper={totalCount > pageSize * 10} // Mostrar jump apenas para muitos itens
            showTotal={(total, range) => `${range[0]}-${range[1]} de ${total} projetos`}
            pageSizeOptions={['12', '24', '48', '96']}
            style={{
              '.ant-pagination-item': {
                borderRadius: 6,
                minWidth: 32,
                height: 32,
                lineHeight: '30px'
              },
              '.ant-pagination-item-active': {
                backgroundColor: theme.primary,
                borderColor: theme.primary
              },
              '.ant-pagination-item-active a': {
                color: '#fff'
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ProjectCardGrid; 